/** InsCoder WANG.J 2026-10-21 B + Save application details only after Globo reveals success, then follow the section redirect **/
(() => {
  if (window.__ogeeProApplicationSuccess) return;
  window.__ogeeProApplicationSuccess = true;
  const instances = new WeakMap();

  const isVisible = (element) => {
    if (!element.isConnected || !element.getClientRects().length) return false;
    for (let node = element; node instanceof Element; node = node.parentElement) {
      const style = getComputedStyle(node);
      if (node.hidden || style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse') return false;
    }
    return true;
  };

  const initialize = (root) => {
    if (instances.has(root)) return;
    const container = root.querySelector('.cust-pro-application__form');
    if (!container) return;
    let pending = null;
    let finished = false;
    let previousVisible = new Set();

    /** InsCoder WANG.J 2026-10-21 B + Format Globo's generated file preview without replacing its upload or removal handlers **/
    const updateUploads = () => {
      container.querySelectorAll('.gfb__dropzone--placeholder--title a').forEach((link) => {
        if (link.getAttribute('role') !== 'button') link.setAttribute('role', 'button');
        if (!link.hasAttribute('tabindex')) link.setAttribute('tabindex', '0');
      });
      container.querySelectorAll('.ogee_pro_file .gfb__dropzone--preview--item').forEach((item) => {
        const title = item.querySelector('.gfb__dropzone--preview--item-title');
        const remove = item.querySelector('[data-file-remove]');
        const filename = item.querySelector('.gfb__dropzone--preview--item-filename')?.textContent.trim();
        if (!title || !remove) return;
        const control = item.closest('.ogee_pro_file');
        const inputs = [...control.querySelectorAll('input[type="file"]')];
        const files = inputs.flatMap((input) => [...input.files]);
        const file = files.find((candidate) => candidate.name === filename);
        if (file) {
          let size = item.querySelector('.gfb__dropzone--preview--item-size');
          if (!size) {
            size = document.createElement('span');
            size.className = 'gfb__dropzone--preview--item-size';
            title.insertBefore(size, remove);
          }
          const value = file.size >= 1048576 ? `${(file.size / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(file.size / 1024))} KB`;
          if (size.textContent !== value) size.textContent = value;
        }
        if (!remove.hasAttribute('data-pro-remove-text')) {
          const label = root.dataset.uploadRemoveLabel || 'Remove';
          remove.dataset.proRemoveText = '';
          remove.append(document.createTextNode(label));
          remove.setAttribute('role', 'button');
          remove.setAttribute('tabindex', '0');
          remove.setAttribute('aria-label', `${label} ${filename || ''}`.trim());
        }
      });
    };
    const onUploadKeydown = (event) => {
      if (!(event.target instanceof Element) || !['Enter', ' '].includes(event.key)) return;
      const browse = event.target.closest('.gfb__dropzone--placeholder--title a');
      if (browse) {
        event.preventDefault();
        browse.click();
        return;
      }
      const remove = event.target.closest('.ogee_pro_file [data-file-remove]');
      if (!remove) return;
      event.preventDefault();
      remove.click();
    };
    /** InsCoder WANG.J 2026-10-21 E + Format Globo's generated file preview without replacing its upload or removal handlers **/

    const messages = () => [...container.querySelectorAll('.globo-form-app .message.success')];
    const capture = (form) => {
      if (finished || !form || window.Shopify?.designMode) return;
      pending = {
        firstName: form.querySelector('[name="text-1"]')?.value.trim() || '',
        email: form.querySelector('[name="email"]')?.value.trim() || ''
      };
      previousVisible = new Set(messages().filter(isVisible));
    };

    const onSubmit = (event) => {
      if (event.target.matches('form') && event.target.closest('.globo-form-app')) capture(event.target);
    };
    const onClick = (event) => {
      if (!(event.target instanceof Element)) return;
      /** InsCoder WANG.J 2026-10-21 B + Let the browse link open Globo's existing file chooser **/
      const browse = event.target.closest('.gfb__dropzone--placeholder--title a');
      if (browse && container.contains(browse)) {
        const uploadButton = browse.closest('.gfb__dropzone')?.querySelector('.gfb__dropzone--placeholder--button');
        if (uploadButton && !uploadButton.disabled) {
          event.preventDefault();
          event.stopPropagation();
          uploadButton.click();
        }
        return;
      }
      /** InsCoder WANG.J 2026-10-21 E + Let the browse link open Globo's existing file chooser **/
      const button = event.target.closest('.globo-form-app button[type="submit"], .globo-form-app button.submit');
      if (button && container.contains(button) && !button.disabled) capture(button.closest('form'));
    };

    const observer = new MutationObserver(() => {
      updateUploads();
      if (!pending || finished) return;
      let succeeded = false;
      for (const message of messages()) {
        if (!isVisible(message)) previousVisible.delete(message);
        else if (!previousVisible.has(message)) succeeded = true;
      }
      if (!succeeded) return;
      try {
        sessionStorage.setItem('ogee-pro-application', JSON.stringify({ ...pending, createdAt: Date.now() }));
      } catch (error) {
        // Keep Globo's success message visible if the browser blocks storage.
        console.error('Unable to save the Ogee PRO application in sessionStorage.', error);
        return;
      }
      finished = true;
      observer.disconnect();
      const destination = root.dataset.successUrl?.trim();
      if (!destination) return;
      try {
        const url = new URL(destination, window.location.href);
        if (url.protocol === 'https:' || url.protocol === 'http:') window.location.assign(url.href);
      } catch (error) {
        console.error('Invalid Ogee PRO success redirect URL.', error);
      }
    });

    container.addEventListener('submit', onSubmit, true);
    container.addEventListener('click', onClick, true);
    container.addEventListener('keydown', onUploadKeydown);
    updateUploads();
    observer.observe(container, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
    instances.set(root, () => {
      observer.disconnect();
      container.removeEventListener('submit', onSubmit, true);
      container.removeEventListener('click', onClick, true);
      container.removeEventListener('keydown', onUploadKeydown);
      instances.delete(root);
    });
  };

  const initializeAll = () => document.querySelectorAll('[data-pro-application]').forEach(initialize);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeAll, { once: true });
  else initializeAll();
  document.addEventListener('shopify:section:load', initializeAll);
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-pro-application]').forEach((root) => instances.get(root)?.());
  });
})();
/** InsCoder WANG.J 2026-10-21 E + Save application details only after Globo reveals success, then follow the section redirect **/
